import React, { useState, useMemo, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { startOfWeek, endOfWeek, addWeeks, subWeeks, eachDayOfInterval, format, differenceInDays, parseISO, isPast, addDays } from 'date-fns';
import WorkPlannerSidebar from './WorkPlannerSidebar';
import WorkPlannerCalendar from './WorkPlannerCalendar';
import { useScheduleTask, useUpdateTaskWorkingPeriod } from 'services/api/task-management/trackerHooks';

const WorkPlanner = ({ tasks: rawTasks = [] }) => {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [customDateRange, setCustomDateRange] = useState(null);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const scheduleTaskMutation = useScheduleTask();
    const updateTaskWorkingPeriodMutation = useUpdateTaskWorkingPeriod();

    const categorizeTask = (task) => {
        if (!task.due_date) return 'upcoming';

        const dueDate = parseISO(task.due_date);
        const today = new Date();
        const daysUntilDue = differenceInDays(dueDate, today);

        if (isPast(dueDate) && daysUntilDue < 0) return 'overdue';
        if (daysUntilDue >= 0 && daysUntilDue <= 3) return 'dueSoon';
        if (daysUntilDue > 3 && daysUntilDue <= 7) return 'thisWeek';
        return 'upcoming';
    };

    const transformedTasks = useMemo(() => {
        return rawTasks.map(task => ({
            id: task.id,
            projectName: task.project?.name || 'Unknown Project',
            taskName: task.name,
            deliverableValue: task.value || 0,
            dueDate: task.due_date ? format(parseISO(task.due_date), 'dd/MM/yyyy') : 'No due date',
            scheduledDate: task.scheduled_date ? format(parseISO(task.scheduled_date), 'yyyy-MM-dd') : null,
            scheduledEndDate: task.scheduled_end_date ? format(parseISO(task.scheduled_end_date), 'yyyy-MM-dd') : null,
            status: task.scheduled_date && task.due_date && !isPast(parseISO(task.due_date)) ? 'ongoing' : categorizeTask(task),
            rawTask: task
        }));
    }, [rawTasks]);

    const getDisplayedDays = () => {
        if (customDateRange) {
            return eachDayOfInterval({
                start: customDateRange.start,
                end: customDateRange.end
            });
        }

        const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 1 });

        return eachDayOfInterval({ start: weekStart, end: weekEnd })
            .filter(day => {
                const dayOfWeek = day.getDay();
                return dayOfWeek !== 0 && dayOfWeek !== 6;
            });
    };

    const workingDays = getDisplayedDays();

    const handlePreviousWeek = () => {
        setCustomDateRange(null);
        setSelectedDate(subWeeks(selectedDate, 1));
    };

    const handleNextWeek = () => {
        setCustomDateRange(null);
        setSelectedDate(addWeeks(selectedDate, 1));
    };

    const handleDateRangeChange = (startDate, endDate) => {
        setCustomDateRange({ start: startDate, end: endDate });
        setSelectedDate(startDate);
    };

    const handleResetToCurrentWeek = () => {
        setCustomDateRange(null);
        setSelectedDate(new Date());
    };

    const handleTaskDrop = async (taskId, newDate) => {
        const formattedDate = format(newDate, 'yyyy-MM-dd');
        // Prevent scheduling tasks that are already submitted for review or completed
        const task = transformedTasks.find(t => t.id === taskId)
        if (task && task.rawTask && (task.rawTask.status === 'submitted_for_review' || task.rawTask.status === 'completed')) {
            setNotification({
                open: true,
                message: 'Cannot schedule a task that has been submitted for review or completed.',
                severity: 'warning'
            })
            return
        }

        try {
            const currentStart = task?.rawTask?.scheduled_date ? parseISO(task.rawTask.scheduled_date) : null
            const currentEnd = task?.rawTask?.scheduled_end_date ? parseISO(task.rawTask.scheduled_end_date) : null
            const durationInDays =
                currentStart && currentEnd
                    ? Math.max(0, differenceInDays(currentEnd, currentStart))
                    : 0
            const newEndDate = addDays(newDate, durationInDays)

            await updateTaskWorkingPeriodMutation.mutateAsync({
                taskId: taskId,
                workingPeriodData: {
                    scheduled_date: formattedDate,
                    scheduled_end_date: format(newEndDate, 'yyyy-MM-dd')
                }
            })

            setNotification({
                open: true,
                message: 'Task scheduled successfully',
                severity: 'success'
            })
        } catch (error) {
            console.error('Failed to schedule task:', error)
            setNotification({
                open: true,
                message: error?.response?.data?.message || 'Failed to schedule task. Please try again.',
                severity: 'error'
            })
        }
    };

    const handleTaskUnschedule = async (taskId) => {
        try {
            await scheduleTaskMutation.mutateAsync({
                taskId: taskId,
                scheduleData: { scheduled_date: null }
            });

            setNotification({
                open: true,
                message: 'Task unscheduled successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Failed to unschedule task:', error);
            setNotification({
                open: true,
                message: 'Failed to unschedule task. Please try again.',
                severity: 'error'
            });
        }
    };

    const categorizedTasks = useMemo(() => {
        const unscheduledTasks = transformedTasks.filter(t => {
            const taskStatus = t.rawTask.status;
            return (!t.scheduledDate || t.status === 'overdue') &&
                taskStatus !== 'completed' &&
                taskStatus !== 'submitted_for_review';
        });

        return {
            overdue: unscheduledTasks.filter(t => t.status === 'overdue'),
            dueSoon: unscheduledTasks.filter(t => t.status === 'dueSoon'),
            thisWeek: unscheduledTasks.filter(t => t.status === 'thisWeek'),
            upcoming: unscheduledTasks.filter(t => t.status === 'upcoming')
        };
    }, [transformedTasks]);

    const scheduledTaskIds = useMemo(() => {
        return new Set(transformedTasks.filter(t => t.scheduledDate).map(t => t.id));
    }, [transformedTasks]);

    const getTasksForDay = (day) => {
        const dateString = format(day, 'yyyy-MM-dd');
        // Exclude tasks that have been submitted for review or completed
        return transformedTasks.filter(task =>
            task.scheduledDate === dateString &&
            task.status !== 'overdue' &&
            task.rawTask &&
            task.rawTask.status !== 'submitted_for_review' &&
            task.rawTask.status !== 'completed'
        )
    };

    useEffect(() => {
        let t
        if (notification.open) {
            t = setTimeout(() => setNotification(n => ({ ...n, open: false })), 5000)
        }
        return () => clearTimeout(t)
    }, [notification.open])

    return (
        <Box sx={{ display: 'flex', gap: 3, height: 'calc(100vh - 200px)', position: 'relative' }}>
            {(scheduleTaskMutation.isLoading || updateTaskWorkingPeriodMutation.isLoading) && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        zIndex: 1000
                    }}
                >
                    <CircularProgress />
                </Box>
            )}

            <Box sx={{ flex: '0 0 25%', minWidth: 280, maxWidth: 350 }}>
                <WorkPlannerSidebar
                    tasks={categorizedTasks}
                    onTaskUnschedule={handleTaskUnschedule}
                />
            </Box>

            <Box sx={{ flex: '1 1 75%', minWidth: 0 }}>
                <WorkPlannerCalendar
                    workingDays={workingDays}
                    getTasksForDay={getTasksForDay}
                    onTaskDrop={handleTaskDrop}
                    onPreviousWeek={handlePreviousWeek}
                    onNextWeek={handleNextWeek}
                    onDateRangeChange={handleDateRangeChange}
                    onResetToCurrentWeek={handleResetToCurrentWeek}
                    selectedDate={selectedDate}
                    customDateRange={customDateRange}
                    scheduledTaskIds={scheduledTaskIds}
                />
            </Box>

            {notification.open && (
                <Box sx={{
                    position: 'fixed',
                    top: 24,
                    left: '70%',
                    transform: 'translateX(-70%)',
                    zIndex: 1400,
                    width: '100%',
                    maxWidth: 700,
                    px: 2
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        backgroundColor:
                            notification.severity === 'success' ? '#F0FAF4' :
                                notification.severity === 'warning' ? '#FFFBEB' :
                                    '#FFF0F0',
                        borderLeft: '4px solid',
                        borderColor:
                            notification.severity === 'success' ? '#4CAF50' :
                                notification.severity === 'warning' ? '#F59E0B' :
                                    '#EF5350',
                        borderRadius: '4px',
                        boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                        py: 1.5,
                        px: 2
                    }}>
                        <CheckCircleIcon sx={{
                            color:
                                notification.severity === 'success' ? '#4CAF50' :
                                    notification.severity === 'warning' ? '#F59E0B' :
                                        '#EF5350',
                            fontSize: 20,
                            flexShrink: 0
                        }} />
                        <Typography sx={{
                            fontSize: 14,
                            fontWeight: 400,
                            color:
                                notification.severity === 'success' ? '#1B5E20' :
                                    notification.severity === 'warning' ? '#78350F' :
                                        '#7A1F1F',
                        }}>
                            {notification.message}
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default WorkPlanner;
